/**
 * Shared Sentry init options for all three runtimes (browser, Node server, edge).
 *
 * Env-gated like the rest of the optional integrations in this repo: when
 * `NEXT_PUBLIC_SENTRY_DSN` is absent the init sites skip `Sentry.init()` entirely,
 * so the SDK is a no-op (no network, no overhead) until a DSN is wired in. Set
 * the DSN as a build-time var (it is inlined into the client bundle).
 *
 * Privacy: this is plain error + performance monitoring — **no Session Replay**
 * and `sendDefaultPii: false`, so it carries no personal data and runs as
 * legitimate-interest monitoring without the cookie-consent gate that PostHog
 * sits behind (see lib/analytics/consent.ts). Add Replay later only behind
 * explicit consent.
 *
 * Per-portal tagging (R12): every event is tagged with the portal id + locale so
 * a Sentry project shared across forks can be filtered/alerted per portal.
 */

import { scrubSentryEvent } from "@/lib/analytics/scrub-url";

export const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

/** Whether Sentry should initialise at all. */
export const SENTRY_ENABLED = Boolean(SENTRY_DSN);

export const sharedSentryOptions = {
  dsn: SENTRY_DSN,
  // Explicit per-deploy environment (e.g. "production", "staging"), falling back
  // to NODE_ENV so local/dev events are still separated from prod.
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ?? process.env.NODE_ENV,
  // Performance traces — sampled. Override per fork/env; 10% is a sane default
  // for a low-to-moderate traffic portal.
  tracesSampleRate: Number(process.env.NEXT_PUBLIC_SENTRY_TRACES_SAMPLE_RATE ?? "0.1"),
  // No PII by default (GDPR — EU portal). Do not flip without a consent story.
  sendDefaultPii: false,
  // A CMS preview token is a credential in the URL; it must not land in an
  // event's request URL, transaction name or navigation breadcrumbs.
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
} as const;

// NOTE: per-portal tags (portal_id / locale) are NOT set here. This module is
// imported by instrumentation-client.ts, so anything it reads from
// `process.env.NEXT_PUBLIC_*` is inlined as a literal into the browser bundle —
// which published the tenant UUID in a public JS chunk. The tags now live in
// ./sentry.runtime-tags, imported only by the Node + edge init sites; that file
// has the full reasoning. Browser events tag nothing (Sentry omits the keys)
// rather than tagging "unknown" from a value the bundle should not carry.
